import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Volume2, Heart, Sun, CloudRain, Flame, Star, Moon, Wind, Send } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useSocket } from '@/hooks/useSocket'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'

const moodOptions = [
  { key: 'hope', icon: Sun, color: '#ff6f61' },
  { key: 'pain', icon: CloudRain, color: '#6366f1' },
  { key: 'love', icon: Heart, color: '#ec4899' },
  { key: 'fear', icon: Moon, color: '#8b5cf6' },
  { key: 'gratitude', icon: Star, color: '#10b981' },
  { key: 'loneliness', icon: Wind, color: '#64748b' },
  { key: 'anger', icon: Flame, color: '#ef4444' },
  { key: 'peace', icon: Sun, color: '#06b6d4' },
]

const reactionTypes = [
  { key: 'hug', emoji: '🤗' },
  { key: 'light', emoji: '✨' },
  { key: 'heart', emoji: '❤️' },
  { key: 'strength', emoji: '💪' },
]

export default function ShoutRoom() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const socket = useSocket()
  const queryClient = useQueryClient()
  const [content, setContent] = useState('')
  const [selectedMood, setSelectedMood] = useState('')
  const [fontSize, setFontSize] = useState(18)
  const scrollRef = useRef<HTMLDivElement>(null)

  const { data: shouts } = useQuery({
    queryKey: ['shouts'],
    queryFn: () => api.get('/shout').then(r => r.data)
  })

  // Real-time shouts
  useEffect(() => {
    if (!socket) return
    const handleNewShout = (shout: any) => {
      queryClient.setQueryData(['shouts'], (old: any) => {
        if (!old) return [shout]
        return [shout, ...old]
      })
    }
    socket.on('shout:new', handleNewShout)
    return () => { socket.off('shout:new', handleNewShout) }
  }, [socket, queryClient])

  const createMutation = useMutation({
    mutationFn: () => api.post('/shout', { content, mood: selectedMood, fontSize, isAnonymous: true }),
    onSuccess: () => {
      setContent('')
      setSelectedMood('')
      setFontSize(18)
      queryClient.invalidateQueries({ queryKey: ['shouts'] })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('shoutRoom.error'), variant: 'destructive' })
  })

  const reactMutation = useMutation({
    mutationFn: ({ id, reaction }: { id: string; reaction: string }) => api.post(`/shout/${id}/react`, { reaction }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shouts'] }),
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [shouts])

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 h-[calc(100vh-4rem)] flex flex-col">
      <div className="text-center mb-4">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-2">
          <Volume2 className="w-6 h-6 text-primary" />
        </div>
        <h1 className="font-display text-xl font-bold">{t('shoutRoom.title')}</h1>
        <p className="text-xs text-muted-foreground">{t('shoutRoom.subtitle')}</p>
      </div>

      {isAuthenticated && (
        <div className="mb-4 p-3 rounded-xl bg-card border border-border">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('shoutRoom.placeholder')}
            maxLength={500}
            className="w-full bg-transparent resize-none focus:outline-none text-sm min-h-[60px]"
            style={{ fontSize: `${fontSize}px` }}
          />
          <div className="flex items-center gap-2 mt-2">
            <div className="flex gap-1 overflow-x-auto scrollbar-hide">
              {moodOptions.map(m => (
                <button
                  key={m.key}
                  onClick={() => setSelectedMood(m.key)}
                  className={`p-1.5 rounded-lg transition-colors ${selectedMood === m.key ? 'ring-2 ring-primary' : ''}`}
                  style={{ backgroundColor: selectedMood === m.key ? m.color + '20' : 'transparent' }}
                >
                  <m.icon className="w-4 h-4" style={{ color: m.color }} />
                </button>
              ))}
            </div>
            <input
              type="range"
              min={12}
              max={32}
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="w-20 accent-primary"
            />
            <button
              onClick={() => createMutation.mutate()}
              disabled={!content.trim() || createMutation.isPending}
              className="ml-auto p-2 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pb-4">
        {shouts?.map((shout: any) => {
          return (
            <motion.div
              key={shout._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-xl bg-card border border-border relative group"
            >
              <p className="mb-3 break-words" style={{ fontSize: `${shout.fontSize || 16}px`, color: shout.color || '#ff6f61' }}>
                {shout.content}
              </p>
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  {reactionTypes.map(r => (
                    <button
                      key={r.key}
                      onClick={() => reactMutation.mutate({ id: shout._id, reaction: r.key })}
                      className="flex items-center gap-1 px-2 py-1 rounded-full bg-secondary/50 text-xs hover:bg-secondary transition-colors"
                      title={t('shoutRoom.reactions.' + r.key)}
                    >
                      <span>{r.emoji}</span>
                      <span>{shout.reactions?.[r.key] || 0}</span>
                    </button>
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">{shout.mood ? t(`shoutRoom.mood.${shout.mood}`) : ''}</span>
              </div>
            </motion.div>
          )
        })}
        {(!shouts || shouts.length === 0) && (
          <div className="text-center py-12 text-muted-foreground text-sm">{t('shoutRoom.emptyState')}</div>
        )}
      </div>
    </div>
  )
}
